/* V25_APPLICATION_UI_USERFLOW_V1 */

export function humanizeToken(value = "") {
  const text = String(value || "")
    .replace(/^capability:/, "")
    .replace(/^CFG:/, "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!text) return "";
  return text.replace(/\b\w/g, (m) => m.toUpperCase());
}

export function conditionLabel(pathway = {}) {
  const raw = String(pathway.condition_id || "");
  const tail = raw.split(":").filter(Boolean).pop() || raw;
  return humanizeToken(tail);
}

export function isMultiRootResultant(state = {}) {
  return state.status === "multi_root_resultant";
}

export function isOrientationCandidate(state = {}) {
  return !isMultiRootResultant(state);
}

export function stateRank(state = {}) {
  const stability = state.stability_class || "";
  if (stability === "robust_multi_root") return 0;
  if (stability === "pair_supported") return 1;
  if (state.status === "multi_root_resultant") return 2;
  return 3;
}

export function sortStates(input = []) {
  return [...input].sort((a, b) => {
    const rank = stateRank(a) - stateRank(b);
    if (rank) return rank;
    const roots = Number(b.independent_root_count || 0) - Number(a.independent_root_count || 0);
    if (roots) return roots;
    return String(a.configuration_label || a.configuration_id || "")
      .localeCompare(String(b.configuration_label || b.configuration_id || ""));
  });
}

export function preferredState(input = []) {
  return sortStates(input)[0] || null;
}

export function groupPathways(pathways = []) {
  const map = new Map();

  for (const pathway of pathways || []) {
    const orientation = pathway.contribution_orientation || "unresolved";
    const conditionId = pathway.condition_id || pathway.id;
    const key = `${orientation}|${conditionId}`;

    if (!map.has(key)) {
      map.set(key, {
        key,
        conditionId,
        label: conditionLabel(pathway),
        orientation,
        pathways: [],
        rootIds: new Set(),
        warrants: [],
        specificity: [],
      });
    }

    const group = map.get(key);
    group.pathways.push(pathway);
    if (pathway.root_id) group.rootIds.add(pathway.root_id);
    if (Number.isFinite(Number(pathway.contribution_warrant))) {
      group.warrants.push(Number(pathway.contribution_warrant));
    }
    if (Number.isFinite(Number(pathway.specificity_score))) {
      group.specificity.push(Number(pathway.specificity_score));
    }
  }

  return [...map.values()]
    .map((group) => ({
      ...group,
      rootIds: [...group.rootIds],
      pathwayCount: group.pathways.length,
      rootCount: group.rootIds.size,
      averageWarrant: group.warrants.length
        ? group.warrants.reduce((a, b) => a + b, 0) / group.warrants.length
        : null,
      averageSpecificity: group.specificity.length
        ? group.specificity.reduce((a, b) => a + b, 0) / group.specificity.length
        : null,
    }))
    .sort((a, b) => {
      const roots = b.rootCount - a.rootCount;
      if (roots) return roots;
      const pathways = b.pathwayCount - a.pathwayCount;
      if (pathways) return pathways;
      return a.label.localeCompare(b.label);
    });
}

export function orientationLabel(value) {
  if (value === "affords") return "Affords";
  if (value === "constrains") return "Constrains";
  if (value === "mixed") return "Mixed";
  return humanizeToken(value || "Unresolved");
}

export function stabilityLabel(value) {
  if (value === "robust_multi_root") return "Robust multi-root";
  if (value === "pair_supported") return "Pair-supported";
  if (value === "single_root_orientation_candidate") return "Orientation candidate";
  return humanizeToken(value || "");
}

export function fmt(value, digits = 2) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
  return Number(value).toFixed(digits);
}
